// Pure state machine for the analytics filter-refresh barrier.
//
// A "filter refresh cycle" starts whenever a filter selection changes. Both the
// side panel (options) and the main screen (KPI + tab data) refetch under one
// cycle; the loaders must appear and disappear together. This module owns the
// barrier logic with no React/DOM dependency so it can be unit-tested directly.
//
// Cross-cycle completions are stale-guarded: an in-flight fetch from an earlier
// cycle finishing late cannot clear the current cycle's pending flag.

export interface RefreshState {
  cycleId: number;
  panelPending: boolean;
  mainPending: boolean;
  // Registered consumers. Counts (not booleans) so React StrictMode's
  // mount/unmount/mount double-invoke and split-view remounts stay balanced.
  panelCount: number;
  mainCount: number;
}

export const initialRefreshState: RefreshState = {
  cycleId: 0,
  panelPending: false,
  mainPending: false,
  panelCount: 0,
  mainCount: 0,
};

export type RefreshAction =
  | { type: 'registerPanel' }
  | { type: 'unregisterPanel' }
  | { type: 'registerMain' }
  | { type: 'unregisterMain' }
  | { type: 'startCycle' }
  | { type: 'endPanel'; cycleId: number }
  | { type: 'endMain'; cycleId: number };

export function refreshReducer(state: RefreshState, action: RefreshAction): RefreshState {
  switch (action.type) {
    case 'registerPanel':
      return { ...state, panelCount: state.panelCount + 1 };
    case 'unregisterPanel':
      return { ...state, panelCount: Math.max(0, state.panelCount - 1) };
    case 'registerMain':
      return { ...state, mainCount: state.mainCount + 1 };
    case 'unregisterMain':
      return { ...state, mainCount: Math.max(0, state.mainCount - 1) };
    case 'startCycle': {
      const cycleId = state.cycleId + 1;
      return {
        ...state,
        cycleId,
        // Arm pending only for consumers that are actually mounted.
        panelPending: state.panelCount > 0,
        mainPending: state.mainCount > 0,
      };
    }
    case 'endPanel':
      // Stale-guard: ignore completions from a superseded cycle.
      if (action.cycleId !== state.cycleId) return state;
      return { ...state, panelPending: false };
    case 'endMain':
      if (action.cycleId !== state.cycleId) return state;
      return { ...state, mainPending: false };
    default:
      return state;
  }
}

// The single loading signal both the panel and the main screen read.
export const selectIsFilterRefreshing = (s: RefreshState): boolean =>
  s.panelPending || s.mainPending;
